import { DetailedList } from '@qbe/ads-blocks-forms';
import { Box, FormGroupLabel, Spacer, Typography } from '@qbe/ads-core';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import SearchOccupationsListItemsField from '~/components/SearchOccupationsListItemsField';
import Loading from '~/components-ds/Loading';
import { useAppContent, useAppDispatch, useBusinessDetails } from '~/hooks';
import { useRTKQErrorHandler } from '~/hooks/useRTKQErrorHandler';
import { useTrackOptimizeActivate } from '~/hooks/useTrackOptimizeActivate';
import { selectIsPropertyOwner } from '~/selectors/selectBusinessDetails';
import { useGetOccupationsListQuery } from '~/services/refData';
import {
  updateIndustryOnly,
  updateOccupation,
  updateOccupationCategoryOnly,
  updateOccupationItem,
  updateOccupationsList,
  updateShowAllOccupationCategories,
} from '~/slices/businessDetails';
import { clearCpiInsured, updateDynamicCoverQuestions } from '~/slices/coverDetails';
import { qalTrackEventAction } from '~/thunks/analytics';
import { selectOccupationAction } from '~/thunks/selectOccupationAction';
import { occupationSearchTypes } from '~/types/analytics';
import { OccupationSearchType, OccupationsListItem } from '~/types/refData';
import { utcDateFormatter } from '~/utils';
import { analyticsConfig } from '~/utils/analytics/analyticsConfig';

import { ContextualHelpWithButton } from '../../components-ds/ContextualHelp/ContextualHelpWithButton';
import { OccupationDescription } from './OccupationDescription';

const QuickSearchOccupationSelector: React.FC = () => {
  const { getTextContent, getJSXContent } = useAppContent({
    basePath: 'dsme.businessDetailsOccupancySearch',
  });

  const dispatch = useAppDispatch();
  const isPropertyOwner = useSelector(selectIsPropertyOwner);
  const {
    industry,
    occupationCategory,
    occupation,
    occupationsList,
    coverStart,
    occupationSearchType,
    occupationItem,
  } = useBusinessDetails();

  const [busy, setBusy] = useState(false);
  const {
    data: occupationsListData,
    error: occupationsListError,
    isFetching: isOccupationsListLoading,
  } = useGetOccupationsListQuery(
    {
      effectiveDate: coverStart ? utcDateFormatter(coverStart) : '',
    },
    { skip: !coverStart }
  );

  // Tracks the "optimize.activate" event when the component mounts or re-renders.
  // This is used to notify Webtrends (or other analytics tools) for visibility tracking
  // and ensures dynamic content is properly handled.
  useTrackOptimizeActivate();

  // Jeff Cheung - set decline state for analytics tracking decline
  const [declineState, setDeclineState] = useState(false);

  useRTKQErrorHandler(occupationsListError);

  useEffect(() => {
    if (occupationsListData) {
      dispatch(updateOccupationsList(occupationsListData.occupations));
    }
  }, [dispatch, occupationsListData]);

  useEffect(() => {
    if (occupation?.occupation && !declineState) {
      dispatch(
        qalTrackEventAction({
          event: 'occupation search',
          eventAction: 'quick search - occupation search success',
          eventLabel: 'occupation search success',
        })
      );
    }
    analyticsConfig.occupationSearch = occupationSearchTypes.quickSearchSuccess;
  }, [occupation?.occupation, declineState, dispatch]);

  const handleSelectItem = async (selectedOccupation: OccupationsListItem) => {
    setDeclineState(false);
    setBusy(true);
    await dispatch(
      selectOccupationAction({
        anzsic: selectedOccupation.code,
        searchType: occupationSearchTypes.quickSearch,
      })
    ).unwrap();
    dispatch(updateOccupationItem(selectedOccupation));
    setBusy(false);
    dispatch(clearCpiInsured()); // if occupation updates reset cpi
    dispatch(updateShowAllOccupationCategories(true));
  };

  const onClearSearch = () => {
    clearCpiInsured();
    dispatch(updateDynamicCoverQuestions([]));
    dispatch(updateIndustryOnly(undefined));
    dispatch(updateOccupationCategoryOnly(undefined));
    dispatch(updateOccupation(undefined));
    dispatch(updateOccupationItem(undefined));
  };

  const scopedOccupationsListItems: Array<OccupationsListItem> = occupationsList
    ? occupationsList.filter((o) => o.propertyOwnerAnzsic)
    : [];

  if (isOccupationsListLoading) return <Loading dataTestId="quick-search__loading" />;

  return (
    <>
      {scopedOccupationsListItems && scopedOccupationsListItems.length > 0 && (
        <Box>
          <FormGroupLabel
            alignCenter
            title={
              isPropertyOwner
                ? getTextContent('fields.quickSearchInput.landlordTitle')
                : getTextContent('fields.quickSearchInput.businessOperatorTitle')
            }
            titleSuffix={
              <ContextualHelpWithButton tracking="help: Please enter your occupation">
                <Typography>
                  {isPropertyOwner
                    ? getJSXContent('contextualhelpers.quickSearchLandlord.description')
                    : getJSXContent('contextualhelpers.quickSearchBusinessOperator.description')}
                </Typography>
              </ContextualHelpWithButton>
            }
          />

          <SearchOccupationsListItemsField
            list={scopedOccupationsListItems}
            selectItem={handleSelectItem}
            onClear={onClearSearch}
            isQuickSearch
          />
        </Box>
      )}

      {!isPropertyOwner && (
        <Box id="occupation-review">
          {busy && <Loading />}
          {!busy && occupation && occupationSearchType === OccupationSearchType.QuickSearch && (
            <>
              <Typography>{getTextContent('fields.quickSearchResult.title')}</Typography>
              <Spacer size="lg" />
              <DetailedList
                variant="stack"
                title="Occupation"
                list={[
                  {
                    id: '1',
                    name: '1',
                    label: getTextContent('fields.quickSearchResult.industryLabel'),
                    value: industry?.industryValue,
                  },
                  {
                    id: '2',
                    name: '2',
                    label: getTextContent('fields.quickSearchResult.categoryLabel'),
                    value: occupationCategory?.categoryDescription,
                  },
                  {
                    id: '3',
                    name: '3',
                    label: getTextContent('fields.quickSearchResult.exactOccupationLabel'),
                    value: occupation?.occupation,
                  },
                ]}
              />
              {occupationItem && (
                <OccupationDescription
                  occupationItem={occupationItem}
                  heading={getTextContent('fields.quickSearchResult.descriptionTitle')}
                />
              )}
              <Typography>{getTextContent('fields.quickSearchResult.helperText')}</Typography>
            </>
          )}
        </Box>
      )}
    </>
  );
};

export default QuickSearchOccupationSelector;
